import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { AuthenticationService } from "@app/services/authentication.service";
import { CountryModel } from "@modules/account/models/country.model";
import { CustomerAdminModel } from "@modules/account/models/customer-admin.model";
import { CustomerInvoiceModel } from "@modules/account/models/customer-invoice.model";
import { LanguageModel } from "@modules/account/models/language.model";
import { VatTypeModel } from "@modules/account/models/vat-type.model";
import { CustomerService } from "@app/services/customer.service";
import { LocationService } from "@app/services/location.service";
import { catchValidationErrors } from "@shared/validation/helpers/catch-validation-errors";
import { ValidationErrors } from "@shared/validation/models/validation-errros.interface";
import { Observable, Subject, of } from "rxjs";
import { filter, map, mergeMap, takeUntil, tap } from "rxjs/operators";
import { BicService } from "@modules/account/services/bic.service";
import { VatType } from "@modules/account/models/vat-type.enum";
import { CreateCustomerCommand } from "@modules/account/models/create-customer.command";
import { NgbActiveModal, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TinaAddressSearchModalComponent } from "@modules/account/tina-address-search-modal/tina-address-search.modal";
import { TinaSearchResult } from "@modules/account/models/tina-search-result.model";
import { FeatureFlagService } from "@app/services/feature-flag.service";
import { FeatureFlags } from "@app/models/feature-flags.enum";
import { CustomerSignalRService } from "@app/services/customer-signalr.service";
import { TelenetIconComponent } from "../../../shared/telenet-icons/telenet-icon.component";
import { ValidationErrorsComponent } from "../../../shared/validation/components/validation-errors.component";
import { CustomMessageComponent } from "../../../shared/messages/custom-message.component";
import { DynamicValidationsHeightDirective } from "../../../shared/directives/dynamic-validations-height.directive";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap/tooltip";
import { FormsModule } from "@angular/forms";
import { PasswordInputComponent } from "../../../shared/password-input/password-input.component";
import { TranslatePipe } from "@ngx-translate/core";
import { EnumTranslationPipe } from "../../../shared/pipes/enum-translation.pipe";

@Component({
   selector: "my-cloud-register-as-new-customer-modal",
   templateUrl: "./register-as-new-customer.modal.html",
   styleUrls: ["./register-as-new-customer.modal.scss"],
   imports: [
      TelenetIconComponent,
      ValidationErrorsComponent,
      CustomMessageComponent,
      DynamicValidationsHeightDirective,
      NgbTooltip,
      FormsModule,
      PasswordInputComponent,
      TranslatePipe,
      EnumTranslationPipe
   ]
})
export class RegisterAsNewCustomerModalComponent implements OnInit, OnDestroy {
   modalRef = inject(NgbActiveModal);
   private customerService = inject(CustomerService);
   private locationService = inject(LocationService);
   authService = inject(AuthenticationService);
   private bicService = inject(BicService);
   private signalRService = inject(CustomerSignalRService);
   private ngbModal = inject(NgbModal);
   private featureFlagService = inject(FeatureFlagService);

   private destroyed: Subject<void> = new Subject<void>();

   public validationErrors?: ValidationErrors;

   public createCustomerCommand: CreateCustomerCommand = {} as CreateCustomerCommand;
   public customerAdminModel: CustomerAdminModel = {} as CustomerAdminModel;
   public customerInvoiceModel: CustomerInvoiceModel = { invoiceCountryCode: "BE" } as CustomerInvoiceModel;

   public countries: Array<CountryModel> = [];
   public languages: Array<LanguageModel> = [];
   public invoicingVatTypes: Array<VatTypeModel> = [];
   public peppolIdentificationSchemes: Array<string> = [];

   public varCustomersCanLogin: boolean = false;
   public bicFetched: boolean = false;
   public customerId: number | null = null;

   public newCustomerIsPushed: boolean = false;
   public isCreating = false;

   /** True when the TinaLocation feature flag is enabled. When disabled, address fields are plain manual inputs. */
   public tinaEnabled: boolean = true;

   public ngOnInit(): void {
      this.featureFlagService
         .isFeatureEnabled(FeatureFlags.TinaLocation)
         .pipe(takeUntil(this.destroyed))
         .subscribe(enabled => {
            this.tinaEnabled = enabled;
         });

      this.locationService
         .searchTranslatedCountries()
         .pipe(takeUntil(this.destroyed))
         .subscribe(countries => {
            this.countries = [...countries.filter(x => x.description)];
         });

      this.locationService
         .getLanguages()
         .pipe(takeUntil(this.destroyed))
         .subscribe(languages => {
            this.languages = [...languages];
         });

      this.customerService
         .getVatTypes()
         .pipe(takeUntil(this.destroyed))
         .subscribe(types => {
            this.invoicingVatTypes = [
               ...types.filter(x => x.id !== VatType.PrivatePerson)
            ];
         });

      this.customerService
         .getPeppolIdentificationSchemes()
         .pipe(takeUntil(this.destroyed))
         .subscribe(schemes => {
            this.peppolIdentificationSchemes = [...schemes];
         });

      this.signalRService
         .on("CustomerCreated")
         .pipe(takeUntil(this.destroyed))
         .subscribe((customerId: string) =>
            this.handleCustomerPush(+customerId)
         );
   }

   public ngOnDestroy(): void {
      this.signalRService.stop();

      if (this.isCreating) {
         alert(
            "De gebruiker wordt nog aangemaakt in de backend. Dit kan tot 2 minuten duren. Je hoeft je niet opnieuw te registreren."
         );
      }

      this.destroyed.next();
      this.destroyed.complete();
   }

   public get invoiceVatLabel(): string {
      return this.customerInvoiceModel.invoiceVatTypeId == 2
         ? "MC_CUSTOMERDETAILS_DETAILS_INVCN"
         : "MC_CUSTOMERDETAILS_DETAILS_INVVAT";
   }

   public get isBeCountry(): boolean {
      return this.customerInvoiceModel?.invoiceCountryCode === "BE";
   }

   public get showPeppol(): boolean {
      return this.isBeCountry;
   }

   // Without the Tina feature the address is entered manually, so the fields stay editable.
   public get showAddressLookup(): boolean {
      return this.isBeCountry && this.tinaEnabled;
   }

   public invoiceCountryCodeChanged(countryCode: string) {
      this.customerInvoiceModel = {
         ...this.customerInvoiceModel,
         invoiceCountryCode: countryCode,
         //Reset peppol fields when country changes
         peppolId: null,
         peppolIdentificationScheme: null,
         peppolBackupEmail: null
      } as CustomerInvoiceModel;

      // The address of a Belgian customer is picked through the Tina lookup, so clear any previous one.
      if (this.showAddressLookup) {
         this.customerInvoiceModel = {
            ...this.customerInvoiceModel,
            invoiceStreet: "",
            invoiceZip: "",
            invoiceLocation: "",
            tinaLocationId: null
         };
      }
   }

   public openAddressLookup(): void {
      const modalRef = this.ngbModal.open(TinaAddressSearchModalComponent, {
         ariaLabelledBy: "modal-basic-title",
         size: "lg",
         scrollable: false,
         backdrop: false,
         modalDialogClass: "modal-secondary"
      });

      modalRef.componentInstance.initialCountryCode =
         this.customerInvoiceModel?.invoiceCountryCode ?? "";

      this.destroyed.subscribe(_ => modalRef.dismiss());

      modalRef.result.then((result: TinaSearchResult) => {
         this.customerInvoiceModel = {
            ...this.customerInvoiceModel,
            invoiceCountryCode:
               result.country || this.customerInvoiceModel.invoiceCountryCode,
            invoiceStreet: `${result.streetName} ${result.houseNumber} ${result.boxNumber}`.trim(),
            invoiceZip: result.postalCode,
            invoiceLocation: result.location,
            tinaLocationId: result.tinaLocationId
         };
      }, _ => void 0);
   }

   public onChangeVatTypeId(): void {
      if (this.customerInvoiceModel.invoiceVatTypeId == VatType.PrivatePerson) {
         this.customerInvoiceModel = {
            ...this.customerInvoiceModel,
            invoiceVat: null
         } as CustomerInvoiceModel;
      }
   }

   public get copyInvoiceInfo(): CustomerAdminModel {
      return (this.customerAdminModel = {
         ...this.customerAdminModel,
         administrativeFirstName: this.customerInvoiceModel.invoiceFirstName,
         administrativeLastName: this.customerInvoiceModel.invoiceLastName,
         administrativeCompanyName: this.customerInvoiceModel
            .invoiceCompanyName,
         administrativeCompanyName2: this.customerInvoiceModel
            .invoiceCompanyName2,
         administrativeStreet: this.customerInvoiceModel.invoiceStreet,
         administrativeStreet2: this.customerInvoiceModel.invoiceStreet2,
         administrativeStreet3: this.customerInvoiceModel.invoiceStreet3,
         administrativeZip: this.customerInvoiceModel.invoiceZip,
         administrativeLocation: this.customerInvoiceModel.invoiceLocation,
         administrativeCountryCode: this.customerInvoiceModel
            .invoiceCountryCode,
         administrativeLanguageCode: this.customerInvoiceModel
            .invoiceLanguageCode,
         administrativePhone: this.customerInvoiceModel.invoicePhone,
         administrativePhone2: this.customerInvoiceModel.invoicePhone2,
         administrativeFax: this.customerInvoiceModel.invoiceFax,
         administrativeEmail: this.customerInvoiceModel.invoiceEmail,
         administrativeVat: this.customerInvoiceModel.invoiceVat,
         administrativeVatTypeId: this.customerInvoiceModel.invoiceVatTypeId
      } as CustomerAdminModel);
   }

   public onFocusoutIban(): void {
      this.getBic().subscribe();
   }

   public save(): void {
      this.checkBicHasValueAndFetchOtherwise()
         .pipe(
            takeUntil(this.destroyed),
            mergeMap(_ => this.createCustomer()),
            tap(customerId => (this.customerId = customerId))
         )
         .subscribe();
   }

   public checkPush(): void {
      if (!this.customerId) {
         this.newCustomerIsPushed = false;
         return;
      }

      this.customerService
         .checkCustomerIsPushed(this.customerId)
         .pipe(
            takeUntil(this.destroyed),
            filter(customerId => customerId !== 0)
         )
         .subscribe(customerId => this.handleCustomerPush(customerId));
   }

   private handleCustomerPush(customerId: number): void {
      console.log(`Customer created with id: ${customerId}`);

      //todo check if customer id is the same as this.customerId for signarlrlrlR (and remove filter above)

      this.isCreating = false;
      this.newCustomerIsPushed = true;

      this.modalRef.close();
   }

   private checkBicHasValueAndFetchOtherwise(): Observable<void> {
      return !this.customerInvoiceModel?.invoiceBic &&
         this.customerInvoiceModel?.invoiceIban
         ? this.getBic()
         : of(void 0);
   }

   private getBic(): Observable<void> {
      return this.bicService.get(this.customerInvoiceModel?.invoiceIban).pipe(
         takeUntil(this.destroyed),
         tap(bicValidation => {
            this.bicFetched = bicValidation.valid;

            if (bicValidation.valid) {
               this.customerInvoiceModel.invoiceBic =
                  bicValidation.bankData.bic;
            }
         }),
         map(_ => void 0)
      );
   }

   private createCustomer(): Observable<number> {
      this.validationErrors = undefined;

      const createCustomerCommand = {
         ...this.createCustomerCommand,
         customerAdminModel: this.copyInvoiceInfo,
         customerInvoiceModel: this.customerInvoiceModel
      } as CreateCustomerCommand;

      return this.customerService.createCustomer(createCustomerCommand).pipe(
         takeUntil(this.destroyed),
         catchValidationErrors(
            errors => (this.validationErrors = { ...errors })
         ),
         tap(_ => (this.isCreating = true))
      );
   }
}
